/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import RouterService from '@ember/routing/router-service';
import ImportModel from 'cio/models/import';
import SegmentModel from 'cio/models/segment';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import joinWords from 'cio/utils/join-words';
import { IdentifierKey } from 'cio/utils/identifiers';

interface ChooseFileArgs {
  import: ImportModel;
  segment: SegmentModel;
}

export default class ChooseFile extends Component<ChooseFileArgs> {
  @service analytics!: AnalyticsService;
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;

  acceptedFileExtensions = ['csv'];

  get availableIdentifiers() {
    // Segment imports aren't based on identifiers
    if (this.args.segment) {
      return 'email or id';
    }

    return joinWords(this.environment.current.enabledIdentifiers.sort(), ' ', ' or ');
  }

  startPreprocessing = task(async () => {
    try {
      await this.args.import.preprocess.perform();

      this.router.transitionTo('segments.segment.imports.csv.setup.preprocessing', this.args.segment, this.args.import);
    } catch (errors) {
      this.apiErrors.report(errors);
    }
  });

  @action
  didUploadCSV({ key, filename }: Record<string, any>) {
    this.args.import.setProperties({
      filename,
      path: key,
      sheet: null,
      error: null,
    });
  }

  @action
  didImportGoogleSheet({ sheetId, sheetName, spreadsheetId, spreadsheetName }: Record<string, any>) {
    this.args.import.setProperties({
      error: null,
      sheet: { sheetId, spreadsheetId, sheetName, spreadsheetName },
    });
  }

  @action
  didDeleteUpload() {
    this.args.import.setProperties({
      filename: null,
      name: null,
      path: null,
      sheet: null,
      error: null,
    });
  }

  @action
  exampleFileClicked() {
    this.analytics.track('import', 'example_link_clicked', { model: this.args.import });
  }

  @action
  setIdMatch() {
    this.args.import.validateRules = {
      ...this.args.import.validateRules,
      identifier: IdentifierKey.Id,
      matchEmail: false,
    };
  }

  @action
  setEmailMatch() {
    this.args.import.validateRules = {
      ...this.args.import.validateRules,
      identifier: IdentifierKey.Email,
      matchEmail: true,
    };
  }
}
